import React from 'react';

// Shared Tailwind button primitive for the plain-HTML (non-MUI) screens. Mirrors the MQBIT
// theme's MuiButton look (theme.ts) — rounded, bold, soft colored shadow, hover-darken — so
// these buttons read as part of the same system instead of flat/bootstrap-style controls.

export type PillButtonVariant = 'primary' | 'success' | 'warning' | 'danger' | 'neutral' | 'outline';
type Size = 'sm' | 'md';

const base =
  'inline-flex items-center justify-center gap-1.5 font-bold border-0 cursor-pointer ' +
  'transition-all duration-150 whitespace-nowrap font-sans ' +
  'disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none';

const sizeClasses: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-lg',
  md: 'px-4 py-2 text-xs rounded-xl',
};

const variantClasses: Record<PillButtonVariant, string> = {
  primary: 'bg-mqbit-600 hover:bg-mqbit-700 text-white shadow-md shadow-mqbit-500/20',
  success: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20',
  warning: 'bg-amber-500 hover:bg-amber-600 text-white shadow-md shadow-amber-500/20',
  danger: 'bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-500/20',
  neutral: 'bg-slate-100 hover:bg-slate-200 text-slate-600',
  outline: 'bg-white hover:bg-slate-50 text-slate-600 border border-solid border-slate-200',
};

export interface PillButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: PillButtonVariant;
  size?: Size;
}

export const PillButton: React.FC<PillButtonProps> = ({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}) => (
  <button className={`${base} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`} {...rest}>
    {children}
  </button>
);
